
import React from "react";
import { useTranslations } from "use-intl";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FilterPanel from "@/components/shared/FilterPanel";
import AmountRangeFilterFields from "@/components/shared/AmountRangeFilterFields";
import type { TransactionFilters as TransactionFiltersType } from "@/types/payments";

interface TransactionFiltersProps {
  filters: TransactionFiltersType;
  handleFilterChange: (key: keyof TransactionFiltersType, value: string) => void;
  resetFilters: () => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  aisEarliestDate?: string;
}

const TransactionFilters = ({
  filters,
  handleFilterChange,
  resetFilters,
  showFilters,
  setShowFilters,
  aisEarliestDate,
}: TransactionFiltersProps) => {
  const t = useTranslations('consultantTransactions.filters');
  return (
    <FilterPanel
      showFilters={showFilters}
      setShowFilters={setShowFilters}
      resetFilters={resetFilters}
      labels={{ show: t('show'), hide: t('hide'), reset: t('reset') }}
    >
      {/* Matched Status Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t('matchStatus')}</label>
        <Select
          value={filters.matched}
          onValueChange={(value) => handleFilterChange('matched', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('selectMatchStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allTransactions')}</SelectItem>
            <SelectItem value="matched">{t('matched')}</SelectItem>
            <SelectItem value="unmatched">{t('unmatched')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Description Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t('description')}</label>
        <Input
          placeholder={t('descriptionPlaceholder')}
          value={filters.description}
          onChange={(e) => handleFilterChange('description', e.target.value)}
        />
      </div>

      <AmountRangeFilterFields
        minAmount={filters.minAmount}
        maxAmount={filters.maxAmount}
        onChange={handleFilterChange}
        labels={{ min: t('minAmount'), max: t('maxAmount') }}
        placeholders={{ min: t('minAmountPlaceholder'), max: t('maxAmountPlaceholder') }}
      />

      {/* Date Filters */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t('startDate')}</label>
        <Input
          type="date"
          value={filters.startDate}
          min={aisEarliestDate}
          onChange={(e) => {
            const value = aisEarliestDate && e.target.value < aisEarliestDate
              ? aisEarliestDate
              : e.target.value;
            handleFilterChange('startDate', value);
          }}
        />
        {aisEarliestDate && (
          <p className="text-xs text-muted-foreground">
            {t('availableFrom', { date: aisEarliestDate })}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('endDate')}</label>
        <Input
          type="date"
          value={filters.endDate}
          onChange={(e) => handleFilterChange('endDate', e.target.value)}
        />
      </div>
    </FilterPanel>
  );
};

export default TransactionFilters;
